import React, { forwardRef, useMemo } from 'react';
import { Badge, Box, IconButton, styled } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { GroupChatIcon } from 'assets';
import { ControlTooltip } from 'components';
import { useChat, useVideo } from 'hooks';
import { IChatContext, IVideoContext } from 'context';

import styles from './ToggleChatButton.module.scss';

type ToggleChatButtonProps = {
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  withTooltip?: boolean;
  onClick?: () => void; // made optional
};

const GroupChatIconStyled = styled(GroupChatIcon)(({ theme }) => ({
  color: '#fff',
}));

export const ToggleChatButton = forwardRef<HTMLButtonElement, ToggleChatButtonProps>(
  ({ disabled, className, iconClassName, withTooltip, onClick }: ToggleChatButtonProps, ref) => {
    const { t } = useTranslation('providerRoom');
    const { conversationsUnreadMessagesData }: IChatContext = useChat();
    const { room }: IVideoContext = useVideo();

    const tooltipMessage = useMemo(() => {
      return withTooltip ? t('chat') : '';
    }, [withTooltip, t]);

    const unreadMessagesCount: number = useMemo(() => {
      return conversationsUnreadMessagesData.get(room?.name ?? '')?.unreadMessagesCount ?? 0;
    }, [conversationsUnreadMessagesData, room]);

    const handleClick = (): void => {
      if (onClick) {
        onClick();
      }
    };

    return (
      <ControlTooltip title={tooltipMessage} isDisabled={disabled}>
        <Box component='span' display='block' className={classnames(styles.deviceButton, className)}>
          <IconButton
            disabled={disabled}
            className={classnames(styles.deviceButton, className)}
            sx={{
              backgroundColor: 'rgb(0 0 0 / 50%) !important',
            }}
            onClick={handleClick}
            edge='end'
            aria-label={t('aria.chat')}
            ref={ref}
          >
            <Badge badgeContent={unreadMessagesCount} className={styles.chatIconBadge} color='primary'>
              <GroupChatIconStyled className={classnames(styles.icon, iconClassName)} />
            </Badge>
          </IconButton>
        </Box>
      </ControlTooltip>
    );
  },
);
